'use client';

import { Button, Grid, SelectField, TextField } from '@petsmart-ui/sparky';

import { CA_PROVINCES, clsx, getNextApiRoute, typedFetch, US_STATES } from '@libs/common-next';
import { getLocale, HttpMethod, isLocaleCA, logger, removeSpecialCharacters } from '@libs/core';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormikContext } from 'formik';
import './AddressForm.scss';
import type { AddressFormInterface } from './types';
import { StreetAddressLine } from './StreetAddressLine';
import { CountryTarget } from '../../constants';

export interface SuggestedAddressInterface {
    format: string;
    matched: Array<number>[];
    suggestion: string;
}

export interface SuggestionResponseInterface {
    totalMatches: number;
    count: number;
    results: SuggestedAddressInterface[];
}

export interface FormattedAddressFieldInterface {
    addressLine1: string;
    addressLine2: string;
    addressLine3: string;
    locality: string;
    province: string;
    postalCode: string;
    country: string;
}

export interface FormatAddressResponseInterface {
    address?: FormattedAddressFieldInterface[];
}

type AddressFormProps = Readonly<{
    addressSuggestionRoute: string;
    aptFieldDefaultOpen?: boolean;
    countryTarget?: CountryTarget;
}>;

export function AddressForm({ addressSuggestionRoute, aptFieldDefaultOpen, countryTarget }: AddressFormProps) {
    const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
    const [suggestedAddresses, setSuggestedAddresses] = useState<SuggestedAddressInterface[] | null>(null);
    const [aptFieldOpen, setAptFieldOpen] = useState(aptFieldDefaultOpen);
    const [dropdownIsOpen, setDropdownIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const locale = getLocale();

    const statesOrProvinces = useMemo(() => {
        if (countryTarget === CountryTarget.BOTH) {
            const combined = [...US_STATES, ...CA_PROVINCES];
            return combined.sort((a, b) => a.name.localeCompare(b.name));
        }
        return isLocaleCA() ? CA_PROVINCES : US_STATES;
    }, [countryTarget]);

    const {
        values,
        handleChange,
        handleBlur,
        touched: { address: touchedAddress },
        errors: { address: addressErrors },
        setValues,
    } = useFormikContext<{ address: AddressFormInterface }>();

    const addressValues = values.address;

    useEffect(() => {
        if (addressValues.apt) {
            setAptFieldOpen(true);
        }
    }, [addressValues.apt]);

    const checkAddressSuggestions = useCallback(
        async (suggestAddressInput: string): Promise<SuggestionResponseInterface | null> => {
            try {
                const response: SuggestionResponseInterface | string | null = await typedFetch(
                    `${getNextApiRoute(
                        'address/suggest-address-edq'
                    )}?query=${suggestAddressInput}&country=${countryTarget}`,
                    {
                        method: HttpMethod.GET,
                        headers: {
                            'content-type': 'application/json',
                        },
                    }
                );

                if (typeof response === 'string') {
                    if (response.indexOf('Access Denied') !== -1) {
                        logger.warn('[checkAddressSuggestions] Akamai returned 403 status code');
                    }

                    return null;
                }

                setLoading(false);

                const suggestions: SuggestedAddressInterface[] | null = response ? response?.results : null;

                setSuggestedAddresses(suggestions);

                return response;
            } catch (e) {
                return Promise.reject(e);
            }
        },
        []
    );

    useEffect(
        () => () => {
            if (timeoutIdRef.current) {
                clearTimeout(timeoutIdRef.current);
            }
        },
        []
    );

    const formatSuggestedAddress = useCallback(
        async (suggestedAddressId: string, country: string | null): Promise<FormatAddressResponseInterface | null> => {
            try {
                return await typedFetch<FormatAddressResponseInterface | null>(
                    `${getNextApiRoute('address/format-address-edq')}?id=${suggestedAddressId}&country=${country}`,
                    {
                        method: HttpMethod.GET,
                        headers: {
                            'content-type': 'application/json',
                        },
                    }
                );
            } catch (e) {
                return Promise.reject(e);
            }
        },
        [addressValues.streetAddress, addressSuggestionRoute, locale]
    );

    const handleSuggestedAddressClick = async (suggestedAddress: SuggestedAddressInterface) => {
        const { searchParams } = new URL(suggestedAddress.format);
        const id = searchParams.get('id');
        const country = searchParams.get('country');

        const formatAddressResponse = id && (await formatSuggestedAddress(id, country));

        if (formatAddressResponse && 'address' in formatAddressResponse) {
            const { address = [] } = formatAddressResponse;

            const formattedAddress = {
                streetAddress: address[0]?.addressLine1,
                apt: address[1]?.addressLine2,
                city: address[3]?.locality,
                state: address[4]?.province,
                zipCode:
                    address[6]?.country?.toUpperCase() === 'UNITED STATES OF AMERICA'
                        ? address[5]?.postalCode.split('-')[0]
                        : address[5]?.postalCode,
            };

            setAptFieldOpen(!!formattedAddress.apt);

            setValues({
                ...values,
                address: {
                    ...values.address,
                    ...formattedAddress,
                },
            });
        }

        setDropdownIsOpen(false);
    };

    return (
        <Grid gap="sm">
            <Grid.Item>
                <StreetAddressLine
                    dropdownIsOpen={dropdownIsOpen}
                    suggestedAddresses={suggestedAddresses}
                    handleSuggestedAddressClick={handleSuggestedAddressClick}
                    loading={loading}
                    setLoading={setLoading}
                    checkAddressSuggestions={checkAddressSuggestions}
                    setDropdownIsOpen={setDropdownIsOpen}
                    setSuggestedAddresses={setSuggestedAddresses}
                />
            </Grid.Item>
            <Grid.Item>
                {aptFieldOpen ? (
                    <TextField
                        data-testid="apt"
                        data-cs-mask
                        name="address.apt"
                        type="text"
                        label="Apt/suite (optional)"
                        value={addressValues.apt}
                        onBlur={handleBlur}
                        onChange={handleChange}
                    />
                ) : (
                    <Button
                        data-testid="apt-btn"
                        aria-expanded="true"
                        className="sparky-u-width-fit-content sparky-u-typography-preset-7"
                        variant="link"
                        iconName="plus"
                        iconSize="md"
                        text="Add apt/suite/other"
                        type="button"
                        onClick={() => setAptFieldOpen(true)}
                    />
                )}
            </Grid.Item>

            <Grid.Item>
                <Grid variant="side-by-side" gap="sm">
                    <Grid.Item>
                        <TextField
                            data-testid="zipCode"
                            data-cs-mask
                            wrapperTestId="zipCode-field"
                            name="address.zipCode"
                            type="text"
                            label={isLocaleCA() ? 'Postal code' : 'Zip code'}
                            value={addressValues.zipCode}
                            isError={touchedAddress?.zipCode && !!addressErrors?.zipCode}
                            onBlur={handleBlur}
                            onChange={(ev: React.ChangeEvent<HTMLInputElement>) => {
                                ev.target.value = removeSpecialCharacters(ev.target.value);
                                handleChange(ev);
                            }}
                            fieldNote={touchedAddress?.zipCode && addressErrors?.zipCode && addressErrors?.zipCode[0]}
                            maxLength={countryTarget !== CountryTarget.BOTH && !isLocaleCA() ? 5 : undefined}
                        />
                    </Grid.Item>
                    <Grid.Item data-testid="state-field">
                        <SelectField
                            className={clsx(addressValues.state && 'sparky-is-filled')}
                            data-testid="state"
                            data-cs-mask
                            name="address.state"
                            items={
                                statesOrProvinces &&
                                statesOrProvinces.map(({ abbr, name }) => ({
                                    label: name,
                                    value: abbr,
                                    disabled: false,
                                }))
                            }
                            label={isLocaleCA() ? 'Province' : 'State'}
                            value={addressValues.state}
                            isError={touchedAddress?.state && !!addressErrors?.state}
                            onBlur={handleBlur}
                            onChange={handleChange}
                            fieldNote={touchedAddress?.state && addressErrors?.state && addressErrors?.state[0]}
                            iconName={
                                touchedAddress?.state && !!addressErrors?.state ? 'exclamation-circle-fill' : undefined
                            }
                        />
                    </Grid.Item>
                </Grid>
            </Grid.Item>

            <Grid.Item>
                <TextField
                    data-testid="city"
                    data-cs-mask
                    wrapperTestId="city-field"
                    name="address.city"
                    type="text"
                    label="City"
                    value={addressValues.city}
                    isError={touchedAddress?.city && !!addressErrors?.city}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    fieldNote={touchedAddress?.city && addressErrors?.city && addressErrors.city[0]}
                />
            </Grid.Item>
        </Grid>
    );
}
